import React, { FunctionComponent, useEffect, useState } from 'react';
import LoginLeftSection from 'components/loginLeftSection/LoginLeftSection';
import Button from 'components/button/Button';
import CheckBox from 'components/checkBox/CheckBox';
import Styles from './StepFour.module.scss';
import { useHistory, useLocation } from 'react-router';
import getItemFromLocalStorage from 'utils/getItemFromLocalStorage';

interface StepFourProps {
  userRegistrationRequest: Function;
  authUser?: { access?: string } | null;
  authError?: string;
  registrationError?: string;
}

const StepFour: FunctionComponent<StepFourProps> = (props: StepFourProps) => {
  const { userRegistrationRequest, authUser, authError, registrationError } = props;
  const [checked, setChecked] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const history = useHistory();
  const location = useLocation<{ profileImage?: File | null }>();
  const profileImage = location.state?.profileImage;

  const handleChange = (event: any) => {
    setChecked(event.target.checked);
  };

  const handleDone = () => {
    const user = getItemFromLocalStorage('userObj');
    if (user === null) {
      return;
    }

    const data = new FormData();
    data.append('name', user.name);
    data.append('email', user.email);
    data.append('password', user.password);
    if (profileImage) {
      data.append('profile_pic', profileImage);
    }
    if (user.token) {
      data.append('token', user.token);
    }

    // Remember the name for the one-time welcome modal shown on the dashboard.
    localStorage.setItem('mp_welcome_name', user.name || '');
    setSubmitting(true);
    // Register only. The saga logs in automatically once registration succeeds
    // (no register/login race), and the effect below navigates once we're
    // authenticated.
    userRegistrationRequest(data);
  };

  // Navigate to the dashboard only after login (chained after registration) has
  // authenticated us — prevents landing on /login due to the old timing race.
  useEffect(() => {
    if (submitting && authUser && authUser.access) {
      localStorage.setItem('mp_welcome_pending', '1');
      history.push('/dashboard');
    }
  }, [submitting, authUser, history]);

  // If registration or login fails, re-enable the button so the user can retry.
  useEffect(() => {
    if (submitting && (authError || registrationError)) {
      setSubmitting(false);
    }
  }, [submitting, authError, registrationError]);

  useEffect(() => {
    if (checked) {
      setDisabled(false);
    } else {
      setDisabled(true);
    }
  }, [checked]);

  return (
    <section className={Styles.customContainer}>
      <div className={Styles.leftSectionWrapper}>
        <LoginLeftSection />
      </div>
      <div className={Styles.rightSectionWrapper}>
        <div className={Styles.loginArea}>
          <div className={Styles.formContent}>
            <div className={Styles.stepBlock}>
              <p className={Styles.stepLabel}>STEP 4</p>
              <p className={Styles.stepSubtitle}>Agree to Moderate Politics guidelines</p>
            </div>

            <div className={Styles.guidelinesBody}>
              <p className={Styles.guidelinesPara}>
                To use Moderate Politics you must be at least 18 years old. The culture of Moderate Politics is
                welcoming and inclusive to everyone. The general conversation guidelines are PG-13.
              </p>
              <p className={Styles.guidelinesPara}>
                Moderate Politics will need to use your approximate location to load nearby chat rooms.
                Allow Moderate Politics to use your location, microphone, and camera in your browser permissions
                to utilize all of the chat room features. Moderate Politics will not ask for your home address
                and we don't recommend disclosing it. If you have any questions post them in the Welcome to
                Moderate Politics chat room and we will help you. Welcome to this great community. We're looking
                forward to chatting with you.
              </p>
              <p className={Styles.guidelinesPara}>
                <strong>Just have fun and join the conversation!</strong>
              </p>
            </div>

            <div className={Styles.checkboxWrapper}>
              <CheckBox
                id="agreeCheckbox"
                label="I agree to the terms and conditions"
                handleChange={handleChange}
              />
            </div>

            {(authError || registrationError) && !submitting && (
              <p style={{ color: '#c0392b', fontSize: 14, marginTop: 8 }}>
                {authError || registrationError}
              </p>
            )}

            <div className={Styles.confirmBtn}>
              <Button
                clickHandler={handleDone}
                label={submitting ? 'PLEASE WAIT…' : 'DONE'}
                disabled={disabled || submitting}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default StepFour;
